import React from 'react';
import {Controller, ControllerProps, FieldPath, FieldValues, RegisterOptions, ValidationRule} from 'react-hook-form';
import TextQuillEditor, {TextQuillEditorProps} from './TextQuillEditor';
import {FormHelperText} from '@mui/material';

export type AppControlledEditorProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<TextQuillEditorProps, 'required' | 'value'> &
    Omit<ControllerProps<TFieldValues, TName>, 'render'> & {
        errorMessage?: string;
        required?: string | ValidationRule<boolean>;
        label: string | React.ReactNode;
    };

export function AppControlledEditor<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({name, control, rules, required, errorMessage, label, ...rest}: AppControlledEditorProps<TFieldValues, TName>) {
    const customeRules: Omit<
        RegisterOptions<TFieldValues, TName>,
        'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'
    > = rules ? rules : {required: required ? `${label} is required` : false};
    return (
        <Controller
            name={name}
            rules={customeRules}
            control={control}
            render={({field, fieldState: {invalid, error}}) => {
                return (
                    <>
                        <TextQuillEditor
                            label={label}
                            required={!!required}
                            {...rest}
                            {...field}
                            value={field.value ?? ''}
                            onChange={(content: string) => {
                                if (rest.onChange) rest.onChange(content);
                                if (field.onChange) {
                                    field.onChange(content);
                                }
                            }}
                        />
                        <FormHelperText sx={{color: 'red'}}>{errorMessage ?? error?.message ?? ''}</FormHelperText>
                    </>
                );
            }}
        />
    );
}

export default AppControlledEditor;
